// Kleiner Verlauf ohne Achsen: die Form zählt, nicht der Wert. Steht in den
// Live-Karten und in der Interface-Tabelle („letzte 5 min").
import type { Sample } from '../lib/types'

export interface SparklineProps {
  points: number[]
  color?: string
  width?: number
  height?: number
  /** Feste Obergrenze; ohne sie richtet sich die Linie nach dem Größtwert. */
  max?: number
  label: string
}

/** Der SVG-Pfad einer Linie. Exportiert, weil sich daran rechnen lässt. */
export function sparkPath(points: number[], width: number, height: number, max?: number): string {
  if (points.length === 0) return ''
  const hoch = max ?? Math.max(...points, 1)
  const spanne = hoch > 0 ? hoch : 1
  if (points.length === 1) {
    const y = height - (Math.min(points[0] ?? 0, spanne) / spanne) * height
    return `M0 ${y.toFixed(1)} L${width} ${y.toFixed(1)}`
  }
  const schritt = width / (points.length - 1)
  return points
    .map((v, i) => {
      const x = i * schritt
      const y = height - (Math.min(v, spanne) / spanne) * height
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')
}

export function Sparkline({
  points,
  color = 'var(--acc)',
  width = 90,
  height = 30,
  max,
  label,
}: SparklineProps) {
  if (points.length === 0) {
    return (
      <span className="muted" style={{ fontSize: 12 }}>
        –
      </span>
    )
  }
  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
      style={{ maxWidth: width }}
    >
      <path
        d={sparkPath(points, width, height, max)}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

/** Die Werte einer Messreihe in zeitlicher Reihenfolge. */
export function werte(samples: Sample[] | undefined): number[] {
  if (!samples) return []
  return [...samples]
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
    .map((s) => s.value)
}
